/**
 * Pure-function D3 timeline renderer.
 * Accepts config + SVG element, renders timeline. Not a React component.
 */

import * as d3 from 'd3';
import type { EventCategory } from '../../constants/eventCategories';
import { EVENT_CATEGORY_HEX_COLORS } from '../../constants/eventStyles';
import { SEVERITY_HEX_COLORS } from '../../constants/eventStyles';
import { getCategorySymbol } from './timelineSymbols';

// --- Interfaces ---

export interface TimelineItem {
  id: string;
  date: Date;
  category: EventCategory;
  severity: string;
  priority?: number;
  title: string;
  status: string;
  isTask: boolean;
  entityIdentifiers?: string[];
  eventType?: string;
}

export interface TimelineRow {
  id: string;
  label: string;
  items: TimelineItem[];
  children?: TimelineRow[];
  isGroup: boolean;
}

export type TimelineMode = 'mini' | 'full';
export type DataMode = 'events' | 'tasks';

export interface TimelineCallbacks {
  onItemClick?: (item: TimelineItem) => void;
  onItemHover?: (item: TimelineItem | null, event?: MouseEvent) => void;
  onRowClick?: (row: TimelineRow) => void;
}

export interface TimelineConfig {
  mode: TimelineMode;
  width: number;
  height: number;
  rows: TimelineRow[];
  dateRange?: [Date, Date];
  categoryFilters?: Set<EventCategory>;
  dataMode: DataMode;
  callbacks?: TimelineCallbacks;
  highlightTopN?: number;
  isDark: boolean;
}

/**
 * Renders an interactive D3 timeline into the given SVG element.
 * Returns a cleanup function.
 */
export function renderTimeline(
  svgEl: SVGSVGElement,
  config: TimelineConfig,
): { cleanup: () => void } {
  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();

  const { mode, width, height, rows, dataMode, callbacks, highlightTopN = 10, isDark } = config;

  // Flatten all items for date extent
  const allItems = rows.flatMap((r) =>
    r.isGroup ? (r.children || []).flatMap((c) => c.items) : r.items
  );
  if (allItems.length === 0) return { cleanup: () => {} };

  // Margins
  const margin = mode === 'mini'
    ? { top: 4, right: 8, bottom: 20, left: 8 }
    : { top: 20, right: 40, bottom: 50, left: 160 };

  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;
  if (innerW <= 0 || innerH <= 0) return { cleanup: () => {} };

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

  // Clip path
  svg.append('defs').append('clipPath').attr('id', 'tl-clip')
    .append('rect').attr('width', innerW).attr('height', innerH);

  const contentGroup = g.append('g').attr('clip-path', 'url(#tl-clip)');

  // Date range
  const dateExtent = config.dateRange || d3.extent(allItems, (d) => d.date) as [Date, Date];
  if (!dateExtent[0] || !dateExtent[1]) return { cleanup: () => {} };
  const timeRange = dateExtent[1].getTime() - dateExtent[0].getTime();
  const paddedStart = new Date(dateExtent[0].getTime() - timeRange * 0.05);
  const paddedEnd = new Date(dateExtent[1].getTime() + timeRange * 0.05);

  // Scales
  const xScale = d3.scaleTime().domain([paddedStart, paddedEnd]).range([0, innerW]);

  // Flatten rows for y-axis (expand groups)
  const flatRows: TimelineRow[] = [];
  rows.forEach((r) => {
    if (r.isGroup && r.children) {
      flatRows.push(r);
      r.children.forEach((c) => flatRows.push(c));
    } else {
      flatRows.push(r);
    }
  });

  const yScale = d3.scaleBand<string>()
    .domain(flatRows.map((r) => r.id))
    .range([0, innerH])
    .padding(mode === 'mini' ? 0.05 : 0.15);

  // Theme colors
  const axisColor = isDark ? '#9CA3AF' : '#6B7280';
  const gridColor = isDark ? '#374151' : '#E5E7EB';

  // Row backgrounds — single-hue teal gradient
  flatRows.forEach((row, i) => {
    const isGroupHeader = row.isGroup;
    const bgColor = isGroupHeader
      ? (isDark ? 'rgba(13, 61, 45, 0.3)' : 'rgba(209, 250, 229, 0.5)')
      : i % 2 === 0
        ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)')
        : 'transparent';

    if (bgColor !== 'transparent') {
      contentGroup.append('rect')
        .attr('x', mode === 'mini' ? 0 : -margin.left)
        .attr('y', yScale(row.id)!)
        .attr('width', mode === 'mini' ? innerW : innerW + margin.left + margin.right)
        .attr('height', yScale.bandwidth())
        .attr('fill', bgColor);
    }
  });

  // Grid (full mode only)
  if (mode === 'full') {
    g.append('g').attr('class', 'grid').attr('opacity', 0.3)
      .call(d3.axisBottom(xScale).ticks(10).tickSize(innerH).tickFormat(() => ''))
      .call((gg) => gg.select('.domain').remove())
      .selectAll('line').style('stroke', gridColor);
  }

  // X-axis
  const xAxis = d3.axisBottom(xScale).ticks(mode === 'mini' ? 4 : 8);
  if (mode === 'full') {
    xAxis.tickFormat(d3.timeFormat('%b %d, %Y') as any);
  } else {
    xAxis.tickFormat(d3.timeFormat('%b %y') as any);
  }
  const xAxisG = g.append('g').attr('class', 'x-axis')
    .attr('transform', `translate(0,${innerH})`).call(xAxis);
  xAxisG.selectAll('text').style('fill', axisColor).style('font-size', mode === 'mini' ? '9px' : '11px');
  xAxisG.selectAll('.domain, .tick line').style('stroke', gridColor);

  // Y-axis (full mode only)
  if (mode === 'full') {
    const yAxisG = g.append('g').attr('class', 'y-axis')
      .call(d3.axisLeft(yScale).tickFormat((d) => {
        const row = flatRows.find((r) => r.id === d);
        if (!row) return d;
        const name = row.label;
        return name.length > 18 ? name.substring(0, 18) + '...' : name;
      }));
    yAxisG.selectAll('text')
      .style('fill', (d) => {
        const row = flatRows.find((r) => r.id === d);
        return row?.isGroup ? (isDark ? '#17c480' : '#059669') : axisColor;
      })
      .style('font-size', '12px')
      .style('font-weight', (d) => {
        const row = flatRows.find((r) => r.id === d);
        return row?.isGroup ? '700' : '500';
      });
    yAxisG.selectAll('.domain, .tick line').style('stroke', gridColor);
  }

  // Determine top-N items by priority for mini mode highlighting
  const topItemIds = new Set<string>();
  if (mode === 'mini' && highlightTopN > 0) {
    const sorted = [...allItems].sort((a, b) => {
      // Sort by severity (critical > high > medium > low), then by recency
      const sevOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
      const aSev = sevOrder[a.severity] ?? 4;
      const bSev = sevOrder[b.severity] ?? 4;
      if (aSev !== bSev) return aSev - bSev;
      return b.date.getTime() - a.date.getTime();
    });
    sorted.slice(0, highlightTopN).forEach((item) => topItemIds.add(item.id));
  }

  // --- Render functions ---

  function renderItems(
    container: d3.Selection<SVGGElement, unknown, null, undefined>,
    xs: d3.ScaleTime<number, number>,
    zoomK: number,
  ) {
    container.selectAll('.tl-item-group').remove();

    const shouldAggregate = mode === 'full' && zoomK < 2;

    flatRows.forEach((row) => {
      if (row.isGroup) return; // Groups are headers, items are on children
      const rowY = yScale(row.id)!;
      const rowH = yScale.bandwidth();
      const items = row.items;
      if (items.length === 0) return;

      const ig = container.append('g').attr('class', 'tl-item-group');

      if (shouldAggregate) {
        renderAggregatedRow(ig, items, xs, rowY, rowH, zoomK);
      } else {
        renderDetailedRow(ig, items, xs, rowY, rowH, zoomK);
      }
    });
  }

  function renderAggregatedRow(
    container: d3.Selection<SVGGElement, unknown, null, undefined>,
    items: TimelineItem[],
    xs: d3.ScaleTime<number, number>,
    rowY: number,
    rowH: number,
    zoomK: number,
  ) {
    const aggregationWidth = Math.max(30, 60 / zoomK);
    const sorted = [...items].sort((a, b) => a.date.getTime() - b.date.getTime());

    interface Bin { startDate: Date; endDate: Date; items: TimelineItem[]; x: number; width: number; }
    const bins: Bin[] = [];
    let currentBin: TimelineItem[] = [];
    let binStart: Date | null = null;

    sorted.forEach((item) => {
      const itemX = xs(item.date);
      if (!binStart) {
        binStart = item.date;
        currentBin = [item];
      } else if (itemX - xs(binStart) < aggregationWidth) {
        currentBin.push(item);
      } else {
        if (currentBin.length > 0) {
          const endDate = currentBin[currentBin.length - 1].date;
          bins.push({
            startDate: binStart, endDate, items: currentBin,
            x: xs(binStart), width: Math.max(6, xs(endDate) - xs(binStart)),
          });
        }
        binStart = item.date;
        currentBin = [item];
      }
    });
    if (currentBin.length > 0 && binStart) {
      const endDate = currentBin[currentBin.length - 1].date;
      bins.push({
        startDate: binStart, endDate, items: currentBin,
        x: xs(binStart), width: Math.max(6, xs(endDate) - xs(binStart)),
      });
    }

    // Determine dominant category color per bin
    const binColor = (bin: Bin) => {
      const cats: Record<string, number> = {};
      bin.items.forEach((it) => { cats[it.category] = (cats[it.category] || 0) + 1; });
      const dominant = Object.entries(cats).sort((a, b) => b[1] - a[1])[0];
      return EVENT_CATEGORY_HEX_COLORS[dominant[0] as EventCategory] || '#6B7280';
    };

    container.selectAll('.agg-rect').data(bins).join('rect')
      .attr('class', 'agg-rect')
      .attr('x', (d) => d.x)
      .attr('y', rowY + rowH * 0.1)
      .attr('width', (d) => Math.max(6, d.width))
      .attr('height', rowH * 0.8)
      .attr('fill', (d) => binColor(d))
      .attr('opacity', 0.7)
      .attr('rx', 4)
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        d3.select(this).attr('opacity', 1).attr('stroke', isDark ? '#fff' : '#000').attr('stroke-width', 1.5);
        showTooltip(event, `
          <div style="font-weight:600;margin-bottom:4px">${d.items.length} events</div>
          <div style="opacity:0.6;font-size:11px">
            ${d3.timeFormat('%b %d, %Y')(d.startDate)} – ${d3.timeFormat('%b %d, %Y')(d.endDate)}
          </div>
        `);
      })
      .on('mouseout', function () {
        d3.select(this).attr('opacity', 0.7).attr('stroke', 'none');
        removeTooltip();
      });

    container.selectAll('.agg-label')
      .data(bins.filter((b) => b.items.length > 1 && b.width > 20))
      .join('text')
      .attr('class', 'agg-label')
      .attr('x', (d) => d.x + d.width / 2)
      .attr('y', rowY + rowH / 2)
      .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
      .attr('fill', '#fff').attr('font-size', '11px').attr('font-weight', '600')
      .attr('pointer-events', 'none')
      .text((d) => d.items.length);
  }

  function renderDetailedRow(
    container: d3.Selection<SVGGElement, unknown, null, undefined>,
    items: TimelineItem[],
    xs: d3.ScaleTime<number, number>,
    rowY: number,
    rowH: number,
    zoomK: number,
  ) {
    const ptSize = mode === 'mini' ? 4 : Math.min(8, 4 + zoomK * 0.5);
    const symbolGen = d3.symbol().size(ptSize * ptSize * 2);

    items.forEach((item) => {
      const itemX = xs(item.date);
      const itemY = rowY + rowH / 2;
      const isMini = mode === 'mini';
      const isHighlighted = !isMini || topItemIds.has(item.id);
      const opacity = isHighlighted ? 0.9 : 0.25;

      const color = dataMode === 'tasks'
        ? (item.status === 'completed' || item.status === 'cancelled' ? '#22C55E' : '#3B82F6')
        : EVENT_CATEGORY_HEX_COLORS[item.category] || '#6B7280';

      const severityColor = SEVERITY_HEX_COLORS[item.severity as keyof typeof SEVERITY_HEX_COLORS] || '#6B7280';

      symbolGen.type(getCategorySymbol(item.category));

      container.append('path')
        .attr('d', symbolGen()!)
        .attr('transform', `translate(${itemX},${itemY})`)
        .attr('fill', severityColor)
        .attr('stroke', color)
        .attr('stroke-width', isMini ? 1 : 2)
        .attr('opacity', opacity)
        .style('cursor', 'pointer')
        .on('mouseover', function (event) {
          d3.select(this).attr('opacity', 1).attr('stroke-width', isMini ? 2 : 3);
          const entitiesHtml = item.entityIdentifiers && item.entityIdentifiers.length > 0
            ? `<div style="font-size:11px;opacity:0.7;margin-top:4px">Components: ${item.entityIdentifiers.join(', ')}</div>`
            : '';
          showTooltip(event, `
            <div style="font-weight:600;margin-bottom:4px">${item.title}</div>
            <div style="opacity:0.7;font-size:12px;margin-bottom:4px">${d3.timeFormat('%b %d, %Y')(item.date)}</div>
            <div style="font-size:11px;opacity:0.6">Severity: ${item.severity}${item.eventType ? ` · ${item.eventType}` : ''}</div>
            ${entitiesHtml}
          `);
          callbacks?.onItemHover?.(item, event as any);
        })
        .on('mouseout', function () {
          d3.select(this).attr('opacity', opacity).attr('stroke-width', isMini ? 1 : 2);
          removeTooltip();
          callbacks?.onItemHover?.(null);
        })
        .on('click', (_event) => {
          callbacks?.onItemClick?.(item);
        });
    });
  }

  // --- Tooltip ---

  function showTooltip(event: any, html: string) {
    removeTooltip();
    d3.select('body').append('div').attr('class', 'timeline-tooltip')
      .style('position', 'absolute')
      .style('background', isDark ? '#1F2937' : '#fff')
      .style('color', isDark ? '#fff' : '#111')
      .style('padding', '10px 14px')
      .style('border-radius', '8px')
      .style('border', `1px solid ${isDark ? '#374151' : '#E5E7EB'}`)
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.15)')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('max-width', '300px')
      .style('left', `${event.pageX + 12}px`)
      .style('top', `${event.pageY - 12}px`)
      .html(html);
  }

  function removeTooltip() {
    d3.selectAll('.timeline-tooltip').remove();
  }

  renderItems(contentGroup, xScale, 1);

  return {
    cleanup: () => { removeTooltip(); },
  };
}
